import _ from 'lodash';
import React, { useMemo, useEffect, useState } from 'react';
import {
  DatetimeField,
  DatetimeSpec,
  DatetimeAnnotation,
} from '@scale/shared/types/params/textcollection';
import { DatetimeFieldAnnotation } from '@scale/shared/types/response/textcollection';
import { Slider } from 'design-system';
import { Select, Option } from 'design-system/Select';
import { makeStyles } from 'design-system/styles';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { FormHelperText } from '@material-ui/core';
import { getHelperText } from './helperText';
import { NewDatePicker } from 'react-scale/components/design-system/DatePicker';
import { MONTHS } from '@scale/shared/dates';
import { useLLMHelper } from 'react-scale/dashboard.scale.com/contexts/LLMHelperContext';

interface DatetimeFieldSectionProps {
  field: DatetimeField;
  annotation: DatetimeFieldAnnotation;
  setAnnotation: (annotation: DatetimeFieldAnnotation) => void;
  disabled?: boolean;
}

export default function DatetimeFieldSection(props: DatetimeFieldSectionProps) {
  const onChange = function (index: number, change: Partial<DatetimeAnnotation>): void {
    const response = props.annotation.response.slice();
    response[index] = {
      ...response[index],
      ...change,
    };
    const annotation: DatetimeFieldAnnotation = {
      ...props.annotation,
      response,
    };
    props.setAnnotation(annotation);
  };
  const classes = useStyles();
  const showDayMonthYearPicker = useMemo(
    () =>
      props.field.include.includes(DatetimeSpec.YEAR) &&
      props.field.include.includes(DatetimeSpec.MONTH) &&
      props.field.include.includes(DatetimeSpec.DAY),
    [props.field.include],
  );

  const llmHelper = useLLMHelper();
  const [aiAssistant, setAiAssistant] = useState<string>('');
  const [aiAssistantLoading, setAiAssistantLoading] = useState<boolean>(false);
  useEffect(() => {
    if (
      llmHelper.aiAssistantEnabled &&
      Object.keys(llmHelper.aiAssistantPrompts).includes(props.field.field_id)
    ) {
      (async () => {
        try {
          setAiAssistantLoading(true);
          const content = await llmHelper.getAiAssistantResponse(props.field.field_id);
          if (content) setAiAssistant(content);
        } finally {
          setAiAssistantLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmHelper.aiAssistantEnabled, llmHelper.aiAssistantPrompts, props.field.field_id]);

  const textFields = props.annotation.response.map(
    (response: DatetimeAnnotation, index: number) => {
      return (
        <div key={index} className={classes.textFieldContainer}>
          {showDayMonthYearPicker && (
            <DayMonthYearPicker
              field={props.field}
              response={response}
              onChange={(change: Partial<DatetimeAnnotation>) => {
                onChange(index, change);
              }}
              disabled={props.disabled}
            />
          )}
          {!showDayMonthYearPicker && props.field.include.includes(DatetimeSpec.YEAR) && (
            <YearSelect
              value={response.year || 0}
              onChange={year => onChange(index, { year })}
              disabled={props.disabled}
            />
          )}
          {!showDayMonthYearPicker && props.field.include.includes(DatetimeSpec.MONTH) && (
            <MonthSelect
              value={response.month || 1}
              onChange={month => onChange(index, { month })}
              disabled={props.disabled}
            />
          )}
          {!showDayMonthYearPicker && props.field.include.includes(DatetimeSpec.DAY) && (
            <DaySelect
              value={response.day || 1}
              onChange={day => onChange(index, { day })}
              disabled={props.disabled}
            />
          )}
          {props.field.include.includes(DatetimeSpec.HOUR) && (
            <HourSlider
              value={response.hour || 0}
              onChange={hour => onChange(index, { hour })}
              disabled={props.disabled}
            />
          )}
          {props.field.include.includes(DatetimeSpec.MINUTE) && (
            <MinuteSlider
              value={response.minute || 0}
              onChange={minute => onChange(index, { minute })}
              disabled={props.disabled}
            />
          )}
        </div>
      );
    },
  );
  return (
    <div>
      <div className={classes.title}>{props.field.title}</div>
      {(!!aiAssistant.length || aiAssistantLoading) && (
        <div className={classes.aiAssistant}>
          {aiAssistantLoading && !aiAssistant.length
            ? 'AI assistant evaluation is loading...'
            : `The AI assistant suggests: ${aiAssistant}`}
        </div>
      )}
      <FormHelperText classes={{ root: classes.helperText }}>
        {getHelperText({ description: props.field.description })}
      </FormHelperText>
      {textFields}
    </div>
  );
}

interface DayMonthYearPickerProps {
  field: DatetimeField;
  response: DatetimeAnnotation;
  onChange: (change: Partial<DatetimeAnnotation>) => void;
  disabled?: boolean;
}

const getInitialDate = (response: DatetimeAnnotation, field: DatetimeField) => {
  const year = response.year || field.defaults?.year;
  const month = response.month || field.defaults?.month;
  const day = response.day || field.defaults?.day;
  if (!_.isUndefined(year) && !_.isUndefined(month) && !_.isUndefined(day)) {
    return new Date(year, month - 1, day);
  }
  return new Date();
};

function DayMonthYearPicker({ response, field, onChange, disabled }: DayMonthYearPickerProps) {
  React.useEffect(() => {
    const initialDate = getInitialDate(response, field);
    onChange({
      year: initialDate.getFullYear(),
      month: initialDate.getMonth() + 1, // zero indexed
      day: initialDate.getDate(),
    });
  }, []);

  return (
    <NewDatePicker
      selected={getInitialDate(response, field)}
      onChange={date => {
        if (date && !Array.isArray(date)) {
          onChange({
            year: date.getFullYear(),
            month: date.getMonth() + 1, // zero indexed
            day: date.getDate(),
          });
        }
      }}
      disabled={disabled}
    />
  );
}

interface YearSelectProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const YearSelect = ({ value, onChange, disabled }: YearSelectProps) => {
  const classes = useStyles();

  const currentYear = new Date().getFullYear();
  const range = (start: number, stop: number, step: number) =>
    Array.from({ length: (stop - start) / step + 1 }, (v, i) => start + i * step);

  // assumption here is that we're working with context of birth year.
  // range of years may need to be modified in the future based on context
  return (
    <div>
      <div className={classes.row}>
        <Select
          value={value}
          onChange={e => {
            onChange(e.target.value as number);
          }}
          disabled={disabled}
        >
          {range(currentYear, currentYear - 100, -1).map((year, index) => (
            <Option value={year} key={index}>
              {year}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

interface MonthSelectProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const MonthSelect = ({ value, onChange, disabled }: MonthSelectProps) => {
  const classes = useStyles();
  return (
    <div>
      <div className={classes.row}>
        <Select
          value={value}
          onChange={e => {
            onChange(e.target.value as number);
          }}
          disabled={disabled}
        >
          {MONTHS.map((month, i) => (
            <Option value={i + 1} key={i}>
              {month}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

interface DaySelectProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const DaySelect = ({ value, onChange, disabled }: DaySelectProps) => {
  const classes = useStyles();
  // this is naive - should use the day-month-year picker if possible
  return (
    <div>
      <div className={classes.row}>
        <Select
          value={value}
          onChange={e => {
            onChange(e.target.value as number);
          }}
          disabled={disabled}
        >
          {_.range(1, 32).map(day => (
            <Option value={day} key={day}>
              {day}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

interface HourSliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

enum HourType {
  AM = 'AM',
  PM = 'PM',
}

const HourSlider = ({ value, onChange, disabled }: HourSliderProps) => {
  const [hourType, setHourType] = React.useState<HourType>(value > 12 ? HourType.PM : HourType.AM);
  const shiftedValue = value - 12 * +(hourType === HourType.PM);
  const onSliderChange = (newValue: number) => {
    onChange(newValue + 12 * +(hourType === HourType.PM));
  };

  const classes = useStyles();

  return (
    <div>
      <div className={classes.row}>
        <span className={classes.timeText}>
          {value === 0 || value === 12 ? 12 : shiftedValue} {hourType}
        </span>
        <div className={classes.slider}>
          <Slider
            position={shiftedValue}
            onAdjust={onSliderChange}
            minVal={0}
            maxVal={11}
            step={1}
            vertical={false}
          />
        </div>
        <Select
          value={hourType}
          onChange={e => {
            setHourType(e.target.value as HourType);
            onChange(shiftedValue + 12 * +(e.target.value === HourType.PM));
          }}
          disabled={disabled}
        >
          {_.values(HourType).map((ht, index) => (
            <Option value={ht} key={index}>
              {ht}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

interface MinuteSliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const MinuteSlider = ({ value, onChange, disabled }: MinuteSliderProps) => {
  const onSliderChange = (newValue: number) => {
    onChange(newValue);
  };
  const classes = useStyles();
  return (
    <div>
      <div className={classes.row}>
        <span className={classes.timeText}>Minute: {value}</span>
        <div className={classes.slider}>
          <Slider
            position={value}
            onAdjust={onSliderChange}
            minVal={0}
            maxVal={59}
            step={1}
            vertical={false}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginBottom: '8px',
  },
  helperText: {
    marginBottom: 8,
  },
  row: {
    marginBottom: 5,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  slider: {
    flexGrow: 1,
    marginRight: 16,
    marginLeft: 16,
  },
  timeText: {
    color: '#000000',
    fontSize: '13px',
    textTransform: 'uppercase',
  },
  textFieldContainer: {
    margin: '0 8px',
  },
  aiAssistant: {
    marginBottom: 8,
    padding: 8,
    whiteSpace: 'pre-wrap',
    backgroundColor: 'rgba(144, 202, 249, .2)',
    borderRadius: 8,
  },
});
